import { useCallback } from 'react';

import { ResponsiveSize } from '~constants';
import { useMediaQuery } from '~hooks';
import { ProductDetailsDesktop } from '~organisms';
import { Product, ProductCategory } from '~types/appsync.types';

import PurchaseFlow from '../PurchaseFlow/purchaseFlow';

interface ProductDetailsProps {
  /** The resolved product category. */
  productCategory?: ProductCategory;
  /** The resolved products list. */
  products?: Product[];
  /** Whether the product category data is loading. */
  isLoadingProductCategory?: boolean;
  /** Category shortname from the HTML attribute — available before productCategory resolves. */
  initialCategoryShortname?: string;
  /** Called when the "Back to results" button is clicked. */
  onBackButtonClick?: () => void;
  /** Whether the desktop purchase drawer is open. */
  isPurchaseDrawerOpen?: boolean;
  /** Called to open the desktop purchase drawer. */
  onOpenPurchaseDrawer?: () => void;
  /** Called to close the desktop purchase drawer. */
  onClosePurchaseDrawer?: () => void;
  /** Called when the drawer close transition finishes (to reset the flow). */
  onExitTransitionEnd?: () => void;
  /** Key used to remount PurchaseFlow after a close (resets all selectors). */
  flowKey?: number;
}

/**
 * Responsive dispatcher for the ProductDetails page.
 *
 * - **Desktop** → `ProductDetailsDesktop` + `PurchaseFlow` (side drawer).
 * - **Mobile**  → `PurchaseFlow` in full-screen drawer mode.
 */
const ProductDetails = ({
  productCategory,
  products,
  isLoadingProductCategory = false,
  initialCategoryShortname,
  onBackButtonClick,
  isPurchaseDrawerOpen = false,
  onOpenPurchaseDrawer,
  onClosePurchaseDrawer,
  onExitTransitionEnd,
  flowKey = 0,
}: ProductDetailsProps) => {
  const isMobile = useMediaQuery(ResponsiveSize.SCREEN_S_MOBILE);

  const handleMobileClose = useCallback(() => {}, []);

  const categoryShortname = initialCategoryShortname || productCategory?.shortname || '';

  if (isMobile) {
    return (
      <PurchaseFlow
        isOpen
        fullScreen
        onClose={handleMobileClose}
        initialCategoryShortname={categoryShortname}
        productCategory={productCategory}
        products={products}
        isLoadingProductCategory={isLoadingProductCategory}
        mobileHeaderTitle={productCategory?.name}
        onBackClick={onBackButtonClick}
      />
    );
  }

  return (
    <>
      <ProductDetailsDesktop
        productCategory={productCategory}
        isLoadingProductCategory={isLoadingProductCategory}
        onBackButtonClick={onBackButtonClick}
        onOpenPurchaseDrawer={onOpenPurchaseDrawer}
      />
      {onClosePurchaseDrawer && (
        <PurchaseFlow
          key={flowKey}
          isOpen={isPurchaseDrawerOpen}
          onClose={onClosePurchaseDrawer}
          onExitTransitionEnd={onExitTransitionEnd}
          initialCategoryShortname={categoryShortname}
          productCategory={productCategory}
          products={products}
          isLoadingProductCategory={isLoadingProductCategory}
        />
      )}
    </>
  );
};

export default ProductDetails;
